import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthShell } from './Login';
import { useAuth } from '../hooks/useAuth';
import { updatePassword, signOut } from '../lib/auth';
import { recoveryLinkError } from '../lib/supabase';

// Opened from the "Reset password" email link. Supabase signs the user in with a
// short-lived recovery session; this page lets them choose a new password.
export default function ResetPassword() {
  const { user, loading } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [error, setError]       = useState('');
  const [done, setDone]         = useState(false);
  const [busy, setBusy]         = useState(false);
  const nav = useNavigate();
  const [params] = useSearchParams();
  const isInvite = params.get('mode') === 'invite';
  const title = isInvite ? 'Welcome — set your password' : 'Choose a new password';

  async function onSubmit(e) {
    e.preventDefault();
    setError('');
    if (password.length < 8)  { setError('Use at least 8 characters.'); return; }
    if (password !== confirm) { setError('The two passwords do not match.'); return; }
    setBusy(true);
    try {
      await updatePassword(password);
      setDone(true);
      setTimeout(() => nav('/', { replace: true }), 1500);
    } catch (err) {
      setError(err.message || 'Could not update password.');
    } finally { setBusy(false); }
  }

  if (loading) {
    return <AuthShell title={title}><div className="text-sm text-slate-500">Checking your link…</div></AuthShell>;
  }

  // Link expired / already used / opened in a different browser
  if (!user) {
    return (
      <AuthShell title="Link not valid">
        <p className="text-sm text-slate-600 mb-4">
          {recoveryLinkError || 'This link is invalid or has expired.'} {isInvite ? 'Ask an admin to send a new invite.' : 'Request a new one from the sign-in page.'}
        </p>
        <Link to="/login" className="btn-primary w-full">Back to sign in</Link>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell title="Password updated">
        <p className="text-sm text-emerald-700">{isInvite ? 'Your password is set.' : 'Your password has been changed.'} Taking you to the CRM…</p>
      </AuthShell>
    );
  }

  return (
    <AuthShell title={title}>
      <form onSubmit={onSubmit} className="space-y-4">
        <p className="text-xs text-slate-500">Signed in as <span className="font-medium">{user.email}</span></p>
        <div>
          <label className="label">New password</label>
          <input type="password" className="input" autoComplete="new-password" value={password} onChange={e => setPassword(e.target.value)} required />
        </div>
        <div>
          <label className="label">Confirm new password</label>
          <input type="password" className="input" autoComplete="new-password" value={confirm} onChange={e => setConfirm(e.target.value)} required />
        </div>
        {error && <div className="text-red-700 text-sm">{error}</div>}
        <button type="submit" disabled={busy} className="btn-primary w-full">
          {busy ? 'Saving…' : 'Save new password'}
        </button>
        <button type="button" onClick={() => signOut().then(() => nav('/login'))} className="text-sm text-slate-500 hover:underline w-full">
          Cancel
        </button>
      </form>
    </AuthShell>
  );
}
